import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { InviteJsonResponse, SectionEmailDomains } from '../../ifaces/user';
import { TutorAvatar } from '../components/TutorAvatar';
import { postJson } from '../util/xhr';
import { useNavigate } from 'react-router';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

export function InvitePage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [bypassInvite, setBypassInvite] = useState<boolean>(false);

  const { mutate: submitInvite, isPending: isUpdating } = useMutation({
    mutationFn: async () =>
      inviteJson({
        email,
        code,
      }),
    onSuccess: (resp: InviteJsonResponse) => {
      if (resp.status && resp.status === 200) {
        setSuccess(t`Invite was accepted! Please check your email for next steps.`);
      } else {
        let errorMsg = t`There was an error with your invite. Please retry or contact support.`;
        if (resp.body?.text) {
          errorMsg = resp.body.text;
        }
        setError(errorMsg);
      }
    },
    onError: (err) => {
      setError(
        err instanceof Error
          ? err.message
          : t`There was an error with your invite. Please retry or contact support.`,
      );
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);
    setError(null);
    submitInvite();
  };

  const handleEmailUpdate = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const emailInput = e.target.value.toLocaleLowerCase();
    setEmail(emailInput);
    if (!emailInput || emailInput === '' || !emailInput.includes('@')) {
      setBypassInvite(false);
      return;
    }

    const emailDomain = emailInput.split('@').pop();
    if (emailDomain && SectionEmailDomains.includes(emailDomain)) {
      setBypassInvite(true);
    } else {
      setBypassInvite(false);
    }
  };

  return (
    <div className="login-box-container">
      <div className="half-login-page half-login-left">
        <img
          alt={t`ProfAI Login`}
          src="/img/loginAbstract.png"
          width={'100%'}
          style={{ borderRadius: '10px 0 0 10px', float: 'left' }}
        />
      </div>
      <div className="half-login-page">
        <div className="invite-form">
          <div className="invite-avatar-holder">
            <TutorAvatar size={80} />
          </div>

          <div className="invite-result-text">
            {isUpdating && (
              <div className="loading-message">
                <Trans>Loading...</Trans>
              </div>
            )}
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}
          </div>

          <div className="invite-form-left-panel" style={{ marginTop: '25px' }}>
            <button
              onClick={() => navigate('/')}
              className="secondary neo large"
              style={{ marginBottom: '30px' }}
            >
              <Trans>BACK</Trans>
            </button>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">
                  <Trans>Email:</Trans>
                </label>
                <input
                  id="email"
                  type="text"
                  value={email}
                  onChange={(e) => handleEmailUpdate(e)}
                  className="app-input form-input"
                  required
                />
              </div>
              {!bypassInvite && (
                <div className="form-group">
                  <label htmlFor="inviteCode">
                    <Trans>Invite Code:</Trans>
                  </label>
                  <input
                    id="inviteCode"
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="app-input form-input"
                    required
                    disabled={bypassInvite}
                  />
                </div>
              )}
              <div className="form-actions" style={{ margin: '40px 0 20px' }}>
                <button type="submit" disabled={isUpdating} className="save-button neo large">
                  {isUpdating ? t`Checking Invite...` : t`Get Started`}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

async function inviteJson(body = {}): Promise<InviteJsonResponse> {
  const response = await postJson(`api/invite`, body);
  const json = await response.json();
  return {
    status: response.status,
    body: json,
  };
}
